import React, { useState, useEffect } from 'react';
import MaterialReactTable from 'material-react-table';
import { Box, Typography, IconButton } from '@mui/material';
import CustomTextField from "../CustomTextField";
import CloseIcon from '@mui/icons-material/Close';


const DecorationMatrixTable = ({ columns, data, onColumnsChange, onDataChange, isEditMode }) => {
  const [editingColumns, setEditingColumns] = useState(columns);
  const handleCellChange = (rowIndex, columnId, value) => {
    const updatedData = data.map((row, index) =>
      index === rowIndex ? { ...row, [columnId]: value } : row
    );
    onDataChange(updatedData);
  };
  const handleHeaderBlur = (colIndex) => {
    const updatedColumns = editingColumns.map((column) => ({
      ...column,
      header: column.header && typeof column.header === 'object'
        ? { ...column.header }
        : column.header,
    }));
    const currentColumn = updatedColumns[colIndex];
    if (!currentColumn?.header || typeof currentColumn.header !== 'object') return;

    const start = Number(currentColumn.header.start);
    const end = Number(currentColumn.header.end);
    if (!Number.isInteger(start) || start < 1 || !Number.isInteger(end) || start > end) {
      // Reject invalid range edits without modifying other boundaries.
      setEditingColumns(columns);
      return;
    }

    if (colIndex > 1) {
      const previous = updatedColumns[colIndex - 1];
      if (previous?.header && typeof previous.header === 'object') {
        const previousStart = Number(previous.header.start);
        if (start <= previousStart) {
          setEditingColumns(columns);
          return;
        }
        previous.header.end = start - 1;
      }
    }
    if (colIndex + 1 < updatedColumns.length) {
      const nextStart = Number(updatedColumns[colIndex + 1]?.header?.start);
      if (Number.isFinite(nextStart) && end >= nextStart) {
        setEditingColumns(columns);
        return;
      }
    }

    // Crucially, do not rebuild every later band when one start changes.
    // The table's original accessorKeys continue to identify its prices.
    onColumnsChange(updatedColumns);
    setEditingColumns(updatedColumns);
  };

  const dynamicColumns = columns.map((col, colIndex) => {

    return ({
      accessorKey: col.accessorKey,

      header: col.accessorKey !== 'Unit' && col.accessorKey !== 'Cost' && typeof col.header === 'object' && col.header !== null ? (
        <Box sx={{ width: '100%', display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {isEditMode ? <Box className="field" sx={{ width: '100px' }}>
            <CustomTextField
              name={col.id}
              placeholder="Price"
              value={col.header?.start}
              handleChange={(e) => {
                setEditingColumns((previous) => previous.map((column, index) =>
                  index === colIndex && column.header && typeof column.header === 'object'
                    ? { ...column, header: { ...column.header, start: e.target.value } }
                    : column
                ));

              }}
              handleBlur={(e) => handleHeaderBlur(colIndex)}
            />
          </Box> : <Typography >{col.header?.start}</Typography>
          }
          <Typography sx={{ marginLeft: '10px' }}>{` - ${col.header?.end}`}</Typography>
        </Box>
      ) : col.accessorKey === 'Cost' || (col.accessorKey !== 'Unit' && typeof col.header !== 'object') ? (
        <Box>
          <Typography sx={{ marginLeft: '10px' }}>{col.header}</Typography>
        </Box>
      ) :
        (
          <Box className="field">
            {isEditMode ? <CustomTextField
              name={col.id}
              placeholder={col.header}
              value={col.header}

              handleChange={(e) => {
                const updatedColumns = [...columns];
                updatedColumns[colIndex] = {
                  ...updatedColumns[colIndex],
                  header: e.target.value
                };
                onColumnsChange(updatedColumns);
              }}
            /> :
              <Typography sx={{ marginLeft: '10px' }}>{col.header}</Typography>
            }
          </Box>
        ),

      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
      minSize: 80,
      maxSize: 120,

      Cell: ({ row, column, setData }) => (
        <Box className="field">
          {isEditMode ? <Box sx={{
            display: 'flex',
          }}>
            {column.accessorKey === 'Unit' && <IconButton className="delete-icon" aria-label="delete" size="large">
              <CloseIcon fontSize="small" />
            </IconButton>}
            <CustomTextField
              name={column.id}
              placeholder={column.header}
              value={row.original[column.id]}
              handleChange={(e) => handleCellChange(row.index, column.id, e.target.value)}
            />
          </Box> : <Typography>{row.original[column.id]}</Typography>}
        </Box>
      )
    });
  });
  if (isEditMode) {
    dynamicColumns.unshift({
      id: 'delete',
      header: '',
      size: 50,
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            '&:hover .delete-icon': {
              visibility: 'visible',
            },
          }}
        >
          <IconButton
            className="delete-icon"
            aria-label="delete"
            onClick={() => {
              const updatedData = [...data];
              updatedData.splice(row.index, 1);
              onDataChange(updatedData);
            }}
            sx={{
              visibility: 'hidden',
              color: 'red',
            }}
            size="large">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    });
  }

  useEffect(() => {
    setEditingColumns(columns);
  }, [columns]);
  const firstColumnId = columns[0]?.accessorKey

  return (
    <Box sx={{
      marginTop: '30px'
    }}>
      <MaterialReactTable
        columns={dynamicColumns}
        data={data}
        enablePagination={false}
        layoutMode="grid-no-grow"
        enableColumnPinning={true}
        enableSorting={false}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableTopToolbar={false}
        enableBottomToolbar={false}

        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}

        muiTableHeadCellProps={({ column }) => {
          const isFirstColumn = column.id === firstColumnId;

          return ({
            sx: {
              backgroundColor: '#f0f0f0',
              fontWeight: 'bold',
              position: isFirstColumn ? 'sticky' : '',
              left: isFirstColumn ? 0 : '',
              backgroundColor: '#f5f5f5',
              zIndex: isFirstColumn ? 2 : '',
              fontWeight: 'bold',

            },
          })
        }}

        muiTableContainerProps={{
          sx: {
            border: '1px solid #ccc',
            borderRadius: '6px',
          },
        }}

        muiTableBodyCellProps={({ column }) => {
          const isFirstColumn = column.id === firstColumnId;
          const isDeleteColumn = column.id === 'delete';
          return ({
            sx: {
              position: isFirstColumn ? 'sticky' : '',
              left: isFirstColumn ? 0 : '',
              zIndex: isFirstColumn ? 1 : '',
              '&:hover .delete-icon': {
                visibility: isDeleteColumn ? 'visible' : 'hidden',
              },
            }
          })
        }}
      />
    </Box>
  );
};

export default DecorationMatrixTable;










